import React, { useEffect, useState } from "react";

import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  FileText,
  LinkIcon,
  Receipt,
  Search,
  Unlink,
} from "lucide-react";

import { AssignPaymentDialog } from "~/components/AssignPaymentDialog";
import { UnlinkPaymentDialog } from "~/components/UnlinkPaymentDialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { BankLogo } from "~/pages/SuperAdminDashboard/BankLogo";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

// Transaction type matching the rail_movements table structure
interface Transaction {
  id: string;
  account_id?: string;
  amount: number;
  currency: string;
  description: string;
  transaction_date: string | null;
  post_date?: string | null;
  created_at?: string;
  sender_account_holder_id: string;
  sender_account_name: string;
  is_linked?: boolean;
  linked_to_type?: "sales_request" | "manual_factura";
  linked_sales_request_id?: string;
  linked_sales_request_ids?: string[]; // Array of all sales request IDs
  linked_sales_request_total?: number;
  linked_manual_factura_id?: string;
  linked_client_name?: string;
  linked_document_info?: string;
  bank_name?: string; // Institution name from rail_banks
  bank_institution_id?: string; // Institution id from rail_banks (for logos)
  total_assigned?: number; // Total amount assigned to payments
  remaining_balance?: number; // Remaining amount available for assignment
}

interface BankOption {
  institutionId: string;
  name: string;
}

interface TransactionsCardProps {
  selectedAccountId: string | null;
  setSelectedAccountId: React.Dispatch<React.SetStateAction<string | null>>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  bankName?: string;
  clientId?: string | null;
  clientTaxId?: string | null; // Add optional clientTaxId prop for filtering by tax ID
}

const TransactionsCard: React.FC<TransactionsCardProps> = ({
  selectedAccountId,
  setSelectedAccountId,
  searchTerm,
  setSearchTerm,
  bankName,
  clientId,
  clientTaxId,
}) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);
  const [selectedSalesRequest, setSelectedSalesRequest] =
    useState<SalesRequest | null>(null);
  const [salesRequestItems, setSalesRequestItems] = useState<
    SalesRequestItem[]
  >([]);
  const [loadingSalesRequestDetails, setLoadingSalesRequestDetails] =
    useState(false);
  const [loadingSalesRequestId, setLoadingSalesRequestId] = useState<
    string | null
  >(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>("todos");
  const [dateRange, setDateRange] = useState<string>("all"); // all, last-week, last-month, etc.
  const [bankFilter, setBankFilter] = useState<string>("all");
  const [bankOptions, setBankOptions] = useState<BankOption[]>([]);

  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Refresh trigger to force re-fetch while maintaining filters
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { toast } = useToast();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch the list of banks for the bank filter (deduped by institution)
  useEffect(() => {
    const fetchBanks = async () => {
      const { data, error } = await supabase
        .from("rail_banks")
        .select("institution_id, institution_name")
        .order("institution_name");

      if (error) {
        console.error("Error fetching banks:", error);
        return;
      }

      const seen = new Set<string>();
      const options: BankOption[] = [];
      (data || []).forEach((bank) => {
        if (!bank.institution_id || seen.has(bank.institution_id)) return;
        seen.add(bank.institution_id);
        options.push({
          institutionId: bank.institution_id,
          name: bank.institution_name,
        });
      });
      setBankOptions(options);
    };

    fetchBanks();
  }, []);

  // Fetch transactions from Supabase
  useEffect(() => {
    const fetchTransactions = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let query = supabase.from("rail_movements").select(
          `
            *,
            rail_bank_accounts!inner(
              rail_banks!inner(
                institution_name,
                institution_id
              )
            )
          `,
          { count: "exact" }
        );

        // Rail cutover: only movements after Fintoc's last day (2026-08-06);
        // earlier movements stay reconciled in the Fintoc archive.
        query = query.gt("post_date", "2026-08-06");

        if (selectedAccountId) {
          query = query.eq("account_id", selectedAccountId);
        } else if (bankFilter !== "all") {
          query = query.eq(
            "rail_bank_accounts.rail_banks.institution_id",
            bankFilter
          );
        }

        // Apply date range filter - use both transaction_date and post_date to include all transactions
        if (dateRange === "last-week") {
          const lastWeek = new Date();
          lastWeek.setDate(lastWeek.getDate() - 7);
          // Include transactions where either transaction_date OR post_date is within range
          query = query.or(
            `transaction_date.gte.${lastWeek.toISOString()},and(transaction_date.is.null,post_date.gte.${lastWeek.toISOString()})`
          );
        } else if (dateRange === "last-month") {
          const lastMonth = new Date();
          lastMonth.setMonth(lastMonth.getMonth() - 1);
          // Include transactions where either transaction_date OR post_date is within range
          query = query.or(
            `transaction_date.gte.${lastMonth.toISOString()},and(transaction_date.is.null,post_date.gte.${lastMonth.toISOString()})`
          );
        }

        // Apply type filter
        if (typeFilter === "ingresos") {
          query = query.gt("amount", 0);
        } else if (typeFilter === "egresos") {
          query = query.lt("amount", 0);
        }

        // Apply search filter if present
        if (debouncedSearchTerm) {
          query = query.or(
            `description.ilike.%${debouncedSearchTerm}%,sender_account_name.ilike.%${debouncedSearchTerm}%,sender_account_holder_id.ilike.%${debouncedSearchTerm}%`
          );
        }

        // Apply pagination
        const from = (currentPage - 1) * pageSize;
        const to = from + pageSize - 1;

        // Get all data first, then sort by effective date (transaction_date || post_date)
        // This ensures Santander transactions (null transaction_date) are sorted by post_date
        const { data: allData, error, count } = await query;

        if (error) throw error;

        // Sort by effective date (transaction_date || post_date) using dayjs
        const sortedData = (allData || []).sort((a, b) => {
          // Get effective date for sorting (transaction_date || post_date)
          const dateA = dayjs(
            a.transaction_date || a.post_date || "1970-01-01"
          );
          const dateB = dayjs(
            b.transaction_date || b.post_date || "1970-01-01"
          );

          // Sort descending (newest first)
          return dateB.valueOf() - dateA.valueOf();
        });

        // Apply pagination after sorting
        const data = sortedData.slice(
          from,
          Math.min(from + pageSize, sortedData.length)
        );

        // Get payment info only for the movements shown on this page.
        // Scoping by id keeps the response well under the PostgREST 10k-row
        // cap (which was silently dropping the newest assignments, making
        // freshly-assigned movements render as "Sin asignar").
        const pageMovementIds = data.map((movement) => movement.id);
        const { data: paymentInfo, error: paymentInfoError } =
          await supabase.rpc("get_transactions_payment_info", {
            p_movement_ids: pageMovementIds,
          });

        if (paymentInfoError) throw paymentInfoError;

        // Payment info already fetched above with the optimized function

        // Calculate total assigned amounts per movement
        const movementTotals = new Map<string, number>();
        paymentInfo?.forEach((payment: any) => {
          if (payment.rail_movement_id) {
            const current = movementTotals.get(payment.rail_movement_id) || 0;
            movementTotals.set(
              payment.rail_movement_id,
              current + (payment.payment_amount || 0)
            );
          }
        });

        // Create maps for linked movement information
        const linkedMovementInfo = new Map<
          string,
          {
            type: "sales_request" | "manual_factura";
            clientName: string;
            documentInfo: string;
            salesRequestId?: string;
            salesRequestIds?: string[]; // Array of all sales request IDs
            salesRequestTotal?: number;
            manualFacturaId?: string;
            totalAssigned?: number;
            remainingBalance?: number;
          }
        >();

        // Group payments by rail_movement_id to collect all sales requests
        const movementPayments = new Map<string, any[]>();
        paymentInfo?.forEach((payment: any) => {
          if (payment.rail_movement_id) {
            if (!movementPayments.has(payment.rail_movement_id)) {
              movementPayments.set(payment.rail_movement_id, []);
            }
            movementPayments.get(payment.rail_movement_id)!.push(payment);
          }
        });

        // Process all payment information from the optimized function
        movementPayments.forEach((payments, fintocMovementId) => {
          const firstPayment = payments[0];
          const salesRequestIds = payments
            .filter((p) => p.sales_request_id)
            .map((p) => p.sales_request_id);

          linkedMovementInfo.set(fintocMovementId, {
            type: firstPayment.payment_type as
              | "sales_request"
              | "manual_factura",
            clientName: firstPayment.client_name || "Cliente desconocido",
            documentInfo: firstPayment.document_info || "Documento desconocido",
            salesRequestId: firstPayment.sales_request_id, // Keep for backward compatibility
            salesRequestIds: salesRequestIds, // Array of all sales request IDs
            salesRequestTotal: firstPayment.sales_request_total,
            manualFacturaId: firstPayment.manual_factura_id,
            totalAssigned: movementTotals.get(fintocMovementId) || 0,
          });
        });

        // Process the transactions data and mark linked transactions
        if (Array.isArray(data)) {
          const processedData = data.map((transaction) => {
            const linkedInfo = linkedMovementInfo.get(transaction.id);
            const totalAssigned = movementTotals.get(transaction.id) || 0;
            const remainingBalance = Math.max(
              0,
              transaction.amount - totalAssigned
            );

            // Extract bank info from joined data
            const bankInfo = transaction.rail_bank_accounts?.rail_banks;
            const bankName = bankInfo?.institution_name || "Unknown Bank";

            // Ensure required fields exist for compatibility with AssignPaymentDialog
            return {
              ...transaction,
              sender_account_name: transaction.sender_account_name || "",
              sender_account_holder_id:
                transaction.sender_account_holder_id || "",
              is_linked: !!linkedInfo,
              linked_to_type: linkedInfo?.type,
              linked_sales_request_id: linkedInfo?.salesRequestId || null,
              linked_sales_request_ids: linkedInfo?.salesRequestIds || [],
              linked_sales_request_total: linkedInfo?.salesRequestTotal || null,
              linked_manual_factura_id: linkedInfo?.manualFacturaId || null,
              linked_client_name: linkedInfo?.clientName,
              linked_document_info: linkedInfo?.documentInfo,
              bank_name: bankName,
              bank_institution_id: bankInfo?.institution_id,
              total_assigned: totalAssigned,
              remaining_balance: remainingBalance,
            };
          });

          // If clientId or clientTaxId is provided, filter to show only:
          // 1. Transactions linked to this client
          // 2. Transactions from this client's tax ID (sender_account_holder_id matches)
          // 3. Unlinked transactions (available for assignment)
          let filteredData = processedData;
          if (clientId || clientTaxId) {
            filteredData = processedData.filter((transaction) => {
              const isLinkedToThisClient =
                transaction.is_linked &&
                linkedMovementInfo.get(transaction.id)?.clientName !==
                  undefined;
              const hasRemainingBalance = transaction.remaining_balance > 0;
              // Clean both tax IDs by removing dots, hyphens, and spaces for comparison
              const cleanClientTaxId =
                clientTaxId?.replace(/[.\-\s]/g, "") || "";
              const cleanSenderTaxId =
                transaction.sender_account_holder_id?.replace(/[.\-\s]/g, "") ||
                "";
              const isFromClientTaxId =
                cleanClientTaxId &&
                cleanSenderTaxId &&
                cleanSenderTaxId === cleanClientTaxId;

              // Only show unassigned transactions that match the client's tax ID
              return !transaction.is_linked && isFromClientTaxId;
            });
          }

          setTransactions(filteredData);
          setTotalCount(
            clientId || clientTaxId ? filteredData.length : count || 0
          );
          setTotalPages(
            Math.max(
              1,
              Math.ceil(
                (clientId || clientTaxId ? filteredData.length : count || 0) /
                  pageSize
              )
            )
          );
        } else {
          throw new Error("Received invalid data format");
        }
      } catch (error: unknown) {
        console.error("Error fetching transactions:", error);
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch transactions"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [
    selectedAccountId,
    currentPage,
    pageSize,
    typeFilter,
    dateRange,
    bankFilter,
    debouncedSearchTerm,
    refreshTrigger,
    clientId,
    clientTaxId,
  ]);

  // Handle page change with validation
  const handlePageChange = (page: number) => {
    // Ensure the page is valid before changing
    if (page > 0 && page <= Math.max(1, totalPages)) {
      setCurrentPage(page);
    }
  };

  const handleAssignPayment = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsAssignDialogOpen(true);
  };

  const handleUnlinkPayment = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsUnlinkDialogOpen(true);
  };

  const handlePaymentsUnlinked = (unlinkedAmount: number) => {
    // Update the specific transaction that had payments unlinked
    if (selectedTransaction) {
      setTransactions((prevTransactions) =>
        prevTransactions.map((transaction) =>
          transaction.id === selectedTransaction.id
            ? {
                ...transaction,
                total_assigned: Math.max(
                  0,
                  (transaction.total_assigned || 0) - unlinkedAmount
                ),
                remaining_balance: Math.min(
                  transaction.amount,
                  (transaction.remaining_balance || 0) + unlinkedAmount
                ),
                is_linked:
                  (transaction.total_assigned || 0) - unlinkedAmount > 0, // Still linked if there are remaining assignments
              }
            : transaction
        )
      );
    }

    setSelectedTransaction(null);
    setIsUnlinkDialogOpen(false);
  };

  const handlePaymentAssigned = (
    salesRequestId?: string,
    assignedAmount?: number
  ) => {
    setIsAssignDialogOpen(false);

    // Update only the specific transaction that was just linked
    if (selectedTransaction && assignedAmount) {
      setTransactions((prevTransactions) =>
        prevTransactions.map((transaction) =>
          transaction.id === selectedTransaction.id
            ? {
                ...transaction,
                is_linked: true,
                linked_sales_request_id: salesRequestId || null,
                linked_sales_request_total: null, // Could be updated if we have the amount
                total_assigned:
                  (transaction.total_assigned || 0) + assignedAmount,
                remaining_balance: Math.max(
                  0,
                  transaction.amount -
                    ((transaction.total_assigned || 0) + assignedAmount)
                ),
              }
            : transaction
        )
      );
    }

    setSelectedTransaction(null);
  };

  const handleViewSalesRequest = async (salesRequestId: string) => {
    if (!salesRequestId) return;

    // Set loading state for this specific sales request ID only
    setLoadingSalesRequestId(salesRequestId);

    try {
      // Fetch the sales request details
      const { data: salesRequestData, error: salesRequestError } =
        await supabase
          .from("sales_request")
          .select("*")
          .eq("id", salesRequestId)
          .single();

      if (salesRequestError) throw salesRequestError;

      // Fetch the sales request items
      const { data: itemsData, error: itemsError } = await supabase
        .from("sales_request_items")
        .select("*, products(short_name)")
        .eq("sales_request_id", salesRequestId);

      if (itemsError) throw itemsError;

      // Format data for the OrderDetailDialog
      const formattedRequest: SalesRequest = {
        id: salesRequestData.id,
        clientId: salesRequestData.client_id,
        createdBy: salesRequestData.created_by,
        approvedRejectedBy: salesRequestData.approved_rejected_by,
        status: salesRequestData.status as any, // Type cast to avoid TypeScript errors
        paid: salesRequestData.paid,
        paymentTerms: salesRequestData.payment_terms,
        paymentType: salesRequestData.payment_type as any, // Type cast to avoid TypeScript errors
        paymentDueDate: salesRequestData.payment_due_date,
        totalAmount: salesRequestData.total_amount,
        comments: salesRequestData.comments,
        deliveryDate: salesRequestData.delivery_date,
        deliveryAddressId: salesRequestData.delivery_address_id,
        deliveryStreet: salesRequestData.delivery_street,
        deliveryNumber: salesRequestData.delivery_number,
        deliveryComplement: salesRequestData.delivery_complement,
        deliveryCity: salesRequestData.delivery_city,
        deliveryState: salesRequestData.delivery_state,
        deliveryComuna: salesRequestData.delivery_comuna,
        rejectedReason: salesRequestData.rejected_reason,
        createdAt: salesRequestData.created_at,
        updatedAt: salesRequestData.updated_at,
        salesConditionId: salesRequestData.sales_condition_id,
      };

      const formattedItems: SalesRequestItem[] = (itemsData || []).map(
        (item) => ({
          id: item.id,
          productId: item.product_id,
          salesRequestId: item.sales_request_id,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercent: item.discount_percent ?? undefined,
          productName: item.products?.short_name || "Producto",
          total: item.quantity * item.unit_price,
        })
      );

      setSelectedSalesRequest(formattedRequest);
      setSalesRequestItems(formattedItems);
    } catch (error) {
      console.error("Error fetching sales request details:", error);
    } finally {
      setLoadingSalesRequestId(null);
    }
  };

  // Mock function for OrderDetailDialog props
  const getSellerInfo = (sellerId: string) => ({
    name: "Vendedor",
    lastname: "",
    email: "",
  });

  const getApproverRejecterInfo = () => ({
    name: "Sistema",
    lastname: "",
    email: "",
    role: "system",
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center text-lg">
            <CreditCard className="mr-2 h-5 w-5" />
            {clientId || clientTaxId
              ? "Transacciones del Cliente"
              : bankName
                ? `Transacciones para ${bankName}`
                : "Transacciones recientes"}
          </CardTitle>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar transacciones"
                className="h-9 w-full border-gray-300 pl-8 pr-2 sm:w-[200px] md:w-[300px]"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
              />
            </div>
            <div className="flex flex-row flex-wrap gap-2 sm:gap-4">
              {!selectedAccountId && (
                <Select
                  value={bankFilter}
                  onValueChange={(value) => {
                    setBankFilter(value);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-9 w-[180px] border-gray-300">
                    <SelectValue placeholder="Banco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los bancos</SelectItem>
                    {bankOptions.map((bank) => (
                      <SelectItem
                        key={bank.institutionId}
                        value={bank.institutionId}
                      >
                        <div className="flex items-center gap-2">
                          <BankLogo
                            institutionId={bank.institutionId}
                            name={bank.name}
                            className="h-4 w-4"
                          />
                          {bank.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9 w-[130px] border-gray-300">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ingresos">Ingresos</SelectItem>
                  <SelectItem value="egresos">Egresos</SelectItem>
                </SelectContent>
              </Select>

              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="h-9 w-[130px] border-gray-300">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="last-week">Last 7 Days</SelectItem>
                  <SelectItem value="last-month">Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Filter buttons at the top of the table */}
        <div className="mb-4 flex flex-wrap gap-2">
          <Button
            variant={typeFilter === "todos" ? "default" : "outline"}
            size="sm"
            className="border-gray-300"
            onClick={() => setTypeFilter("todos")}
          >
            Todos
          </Button>
          <Button
            variant={typeFilter === "ingresos" ? "default" : "outline"}
            size="sm"
            className="border-gray-300"
            onClick={() => setTypeFilter("ingresos")}
          >
            Ingresos
          </Button>
          <Button
            variant={typeFilter === "egresos" ? "default" : "outline"}
            size="sm"
            className="border-gray-300"
            onClick={() => setTypeFilter("egresos")}
          >
            Egresos
          </Button>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="rounded-lg border border-gray-300">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1020px] border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="w-[90px] border px-3 py-2">Fecha</th>
                    <th className="w-[220px] border px-3 py-2">Descripción</th>
                    <th className="w-[120px] border px-3 py-2">RUT</th>
                    <th className="w-[120px] border px-3 py-2 text-center">
                      Monto
                    </th>
                    <th className="w-[120px] border px-3 py-2">Estado</th>
                    <th className="w-[120px] border px-3 py-2">
                      Nota de Venta
                    </th>
                    <th className="w-[120px] border px-3 py-2">
                      Factura Manual
                    </th>
                    <th className="w-[120px] border px-3 py-2">Banco</th>
                    <th className="w-[60px] border px-3 py-2">Accionables</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 8 }).map((_, r) => (
                      <tr key={r}>
                        {Array.from({ length: 9 }).map((_, c) => (
                          <td key={c} className="border px-4 py-2">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : error ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="border px-4 py-8 text-center text-red-600"
                      >
                        Error: {error}
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="border px-4 py-8 text-center text-gray-500"
                      >
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((transaction, index) => {
                      const isIncoming = transaction.amount > 0;

                      return (
                        <tr
                          key={transaction.id}
                          style={{
                            animationDelay: `${Math.min(index * 30, 360)}ms`,
                          }}
                          className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                        >
                          <td className="border px-4 py-2">
                            <span className="text-xs">
                              {transaction.transaction_date
                                ? dayjs(transaction.transaction_date).format(
                                    "DD/MM/YY"
                                  )
                                : transaction.post_date
                                  ? dayjs(transaction.post_date)
                                      .utc()
                                      .format("DD/MM/YY")
                                  : ""}
                            </span>
                          </td>
                          <td className="border px-4 py-2">
                            <div className="group relative w-[200px] truncate">
                              {transaction.description}
                              <div className="absolute left-0 top-full z-10 hidden w-max max-w-xs rounded bg-gray-900 px-2 py-1 text-sm text-white group-hover:block">
                                {transaction.description}
                              </div>
                            </div>
                          </td>
                          <td className="border px-4 py-2">
                            <span className="font-mono">
                              {transaction.sender_account_holder_id || "-"}
                            </span>
                          </td>
                          <td className="border px-4 py-2 text-right">
                            <span
                              className={`tabular-nums ${isIncoming ? "text-green-600" : "text-red-600"}`}
                            >
                              {formatCurrency(Math.abs(transaction.amount))}
                            </span>
                          </td>
                          <td className="border px-4 py-2">
                            {transaction.is_linked ? (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1">
                                  {transaction.linked_to_type ===
                                  "sales_request" ? (
                                    <>
                                      <FileText className="h-4 w-4 text-blue-600" />
                                      <Badge
                                        variant="outline"
                                        className="bg-blue-100 text-blue-800"
                                      >
                                        Nota de Venta
                                      </Badge>
                                    </>
                                  ) : transaction.linked_to_type ===
                                    "manual_factura" ? (
                                    <>
                                      <Receipt className="h-4 w-4 text-green-600" />
                                      <Badge
                                        variant="outline"
                                        className="bg-green-100 text-green-800"
                                      >
                                        Factura Manual
                                      </Badge>
                                    </>
                                  ) : (
                                    // Legacy display for payments without linked_to_type
                                    <Badge
                                      variant="outline"
                                      className="bg-green-100 text-green-800"
                                    >
                                      Asignado
                                    </Badge>
                                  )}
                                </div>
                                {transaction.total_assigned !== undefined &&
                                  transaction.remaining_balance !== undefined &&
                                  transaction.remaining_balance > 0 && (
                                    <div className="text-xs text-orange-600">
                                      Asignado:{" "}
                                      {formatCurrency(
                                        transaction.total_assigned
                                      )}
                                      <br />
                                      Disponible:{" "}
                                      {formatCurrency(
                                        transaction.remaining_balance
                                      )}
                                    </div>
                                  )}
                              </div>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-yellow-100 text-yellow-800"
                              >
                                Sin asignar
                              </Badge>
                            )}
                          </td>
                          <td className="border px-4 py-2">
                            {transaction.is_linked &&
                            transaction.linked_sales_request_ids &&
                            transaction.linked_sales_request_ids.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {transaction.linked_sales_request_ids.length ===
                                1 ? (
                                  // Single sales request - show button with hover tooltip
                                  <div className="group relative">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleViewSalesRequest(
                                          transaction
                                            .linked_sales_request_ids![0]
                                        )
                                      }
                                      className="text-xs"
                                    >
                                      <span className="text-blue-600">
                                        #
                                        {transaction.linked_sales_request_ids![0].substring(
                                          0,
                                          8
                                        )}
                                        ...
                                      </span>
                                      <div className="absolute left-0 top-full z-10 hidden w-max max-w-xs rounded bg-gray-900 px-2 py-1 text-sm text-white group-hover:block">
                                        $
                                        {formatCurrency(
                                          transaction.linked_sales_request_total ||
                                            0
                                        )}
                                      </div>
                                    </Button>
                                  </div>
                                ) : (
                                  // Multiple sales requests - show dropdown
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="flex items-center gap-1 text-xs"
                                      >
                                        <span className="text-blue-600">
                                          {
                                            transaction.linked_sales_request_ids
                                              .length
                                          }{" "}
                                          Notas de Venta
                                        </span>
                                        <ChevronDown className="h-3 w-3" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="start"
                                      className="max-h-[300px] overflow-y-auto"
                                    >
                                      {transaction.linked_sales_request_ids.map(
                                        (salesRequestId) => (
                                          <DropdownMenuItem
                                            key={salesRequestId}
                                            onClick={() =>
                                              handleViewSalesRequest(
                                                salesRequestId
                                              )
                                            }
                                            className="cursor-pointer"
                                          >
                                            <span className="text-blue-600">
                                              #{salesRequestId.substring(0, 8)}
                                              ...
                                            </span>
                                          </DropdownMenuItem>
                                        )
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            ) : transaction.is_linked &&
                              transaction.linked_sales_request_id ? (
                              // Fallback for backward compatibility
                              <div className="group relative">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleViewSalesRequest(
                                      transaction.linked_sales_request_id!
                                    )
                                  }
                                  className="text-xs"
                                >
                                  <span className="text-blue-600">
                                    #
                                    {transaction.linked_sales_request_id.substring(
                                      0,
                                      8
                                    )}
                                    ...
                                  </span>
                                  <div className="absolute left-0 top-full z-10 hidden w-max max-w-xs rounded bg-gray-900 px-2 py-1 text-sm text-white group-hover:block">
                                    $
                                    {formatCurrency(
                                      transaction.linked_sales_request_total ||
                                        0
                                    )}
                                  </div>
                                </Button>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="border px-4 py-2">
                            {transaction.is_linked &&
                            transaction.linked_manual_factura_id ? (
                              <div className="flex flex-col">
                                <div className="text-sm font-medium text-green-600">
                                  {transaction.linked_manual_factura_id.substring(
                                    0,
                                    8
                                  )}
                                  ...
                                </div>
                                <div className="text-xs text-gray-500">
                                  ID de Factura
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="border px-4 py-2">
                            <div className="flex items-center gap-2">
                              <BankLogo
                                institutionId={transaction.bank_institution_id}
                                name={transaction.bank_name}
                                className="h-4 w-4"
                              />
                              <span className="text-sm text-gray-600">
                                {transaction.bank_name || "Unknown"}
                              </span>
                            </div>
                          </td>
                          <td className="border px-4 py-2">
                            {isIncoming && (
                              <div className="flex items-center gap-1">
                                {transaction.is_linked && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleUnlinkPayment(transaction)
                                    }
                                    title="Desvincular pago"
                                  >
                                    <Unlink className="h-4 w-4" />
                                  </Button>
                                )}
                                {(!transaction.is_linked ||
                                  (transaction.remaining_balance &&
                                    transaction.remaining_balance > 0)) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleAssignPayment(transaction)
                                    }
                                    title={
                                      transaction.is_linked
                                        ? "Asignar saldo restante"
                                        : "Asignar pago"
                                    }
                                  >
                                    <LinkIcon className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination controls */}
        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              className="border-gray-300"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-gray-300"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <span className="px-2 text-sm">
              Page {currentPage} of {totalPages}
            </span>

            <Button
              variant="outline"
              size="sm"
              className="border-gray-300"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-gray-300"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>

          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-[70px] border-gray-300">
              <SelectValue placeholder="100" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>

      {/* Payment Assignment Dialog */}
      {selectedTransaction && (
        <AssignPaymentDialog
          open={isAssignDialogOpen}
          onOpenChange={setIsAssignDialogOpen}
          transaction={selectedTransaction}
          onPaymentAssigned={handlePaymentAssigned}
        />
      )}

      {/* Unlink Payment Dialog */}
      {selectedTransaction && (
        <UnlinkPaymentDialog
          open={isUnlinkDialogOpen}
          onOpenChange={setIsUnlinkDialogOpen}
          transaction={selectedTransaction}
          onPaymentsUnlinked={handlePaymentsUnlinked}
        />
      )}

      {/* Sales Request Detail Dialog */}
      {selectedSalesRequest && (
        <OrderDetailDialog
          selectedOrder={selectedSalesRequest}
          setSelectedOrder={setSelectedSalesRequest}
          getSellerInfo={getSellerInfo}
          getApproverRejecterInfo={getApproverRejecterInfo}
          clientNames={{ [selectedSalesRequest.clientId]: "Cliente" }}
          items={salesRequestItems}
          onOrderStatusChange={() => {
            // Refresh transactions after any changes
            setCurrentPage(1);
          }}
        />
      )}
    </Card>
  );
};

export default TransactionsCard;
